import { Profile } from "../types";
import { SectionHeader } from "../SectionHeader";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ContactCard } from "./contact/ContactCard";
import { PolicyCreationForm } from "./policy/PolicyCreationForm";
import { PolicyList } from "./policy/PolicyList";
import { Policy } from "./policy/types";

interface ContactInfoSectionProps {
  profile: Profile;
  isVisible: boolean;
  onToggle: (sectionName: string) => void;
  sectionVisibility: Record<string, boolean>;
  toggleSectionVisibility: (sectionName: string) => void;
}

export const ContactInfoSection = ({
  profile,
  isVisible,
  onToggle,
  sectionVisibility,
  toggleSectionVisibility,
}: ContactInfoSectionProps) => {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const { toast } = useToast();

  const fetchPolicies = async () => {
    try {
      const { data, error } = await supabase
        .from('policies')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPolicies((data || []) as Policy[]);
    } catch (error) {
      console.error('Error fetching policies:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch policies",
      });
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, [profile.id]);

  const handlePolicyCreated = async () => {
    await fetchPolicies();
    // Ensure the policies section is visible after creation
    if (!sectionVisibility["policies"]) {
      toggleSectionVisibility("policies");
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-white to-purple-50 rounded-lg border border-purple-100/50 shadow-lg transition-all duration-300">
        <div className="p-6">
          <SectionHeader
            title="Contact Information"
            sectionName="contact_info"
            isVisible={isVisible}
            onToggle={onToggle}
          />
          {isVisible && <ContactCard profile={profile} refetch={fetchPolicies} />}
        </div>
      </div>

      <div className="bg-gradient-to-br from-white to-purple-50 rounded-lg border border-purple-100/50 shadow-lg transition-all duration-300">
        <div className="py-12 px-6">
          <PolicyCreationForm userId={profile.id} onPolicyCreated={handlePolicyCreated} />
        </div>
      </div>

      <PolicyList 
        policies={policies} 
        isVisible={sectionVisibility["policies"] ?? true}
        onToggle={toggleSectionVisibility}
      />
    </div>
  );
};